import type { MdmcRecord } from '../types';

const DB_NAME = 'matter-dashboard-mobile';
const DB_VERSION = 1;
const RECORDS = 'records';
const OUTBOX = 'outbox';

export interface OutboxMutation {
  id: string;
  record: MdmcRecord;
  createdAt: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(RECORDS)) db.createObjectStore(RECORDS, { keyPath: ['entity_type', 'entity_id'] });
      if (!db.objectStoreNames.contains(OUTBOX)) db.createObjectStore(OUTBOX, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transaction<T>(storeName: string, mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(db => new Promise<T>((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const request = run(tx.objectStore(storeName));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  }));
}

export async function cacheRecords(records: MdmcRecord[]): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(RECORDS, 'readwrite');
    const store = tx.objectStore(RECORDS);
    store.clear();
    for (const record of records) store.put(record);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

export async function readCachedRecords(): Promise<MdmcRecord[]> {
  return transaction<MdmcRecord[]>(RECORDS, 'readonly', store => store.getAll());
}

export async function putCachedRecord(record: MdmcRecord): Promise<void> {
  await transaction<IDBValidKey>(RECORDS, 'readwrite', store => store.put(record));
}

export async function queueMutation(record: MdmcRecord): Promise<void> {
  const mutation: OutboxMutation = {
    id: `${record.entity_type}:${record.entity_id}`,
    record,
    createdAt: new Date().toISOString(),
  };
  await transaction<IDBValidKey>(OUTBOX, 'readwrite', store => store.put(mutation));
}

export async function readOutbox(): Promise<OutboxMutation[]> {
  return transaction<OutboxMutation[]>(OUTBOX, 'readonly', store => store.getAll());
}

export async function removeOutboxItem(id: string): Promise<void> {
  await transaction<undefined>(OUTBOX, 'readwrite', store => store.delete(id));
}

export async function countOutbox(): Promise<number> {
  return transaction<number>(OUTBOX, 'readonly', store => store.count());
}

export async function clearLocalCache(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([RECORDS, OUTBOX], 'readwrite');
    tx.objectStore(RECORDS).clear();
    tx.objectStore(OUTBOX).clear();
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}
